import type MarkdownIt from 'markdown-it';
import type StateCore from 'markdown-it/lib/rules_core/state_core.mjs';
import type Token from 'markdown-it/lib/token.mjs';

import { isSupportedByLinkingPlatform } from '../../linking-platform/utils';

const linkPatternStart = '\\bhttps?:\\/\\/(?:www\\.)?';
const linkPatternEnd = '[^\\s\\]\\)]*';

// If we want to re-introduce canva support, please use the regex from this
// PR which was made redundant: https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/pull-requests/135407/commits/0fcf626f035396cfcb87153a4d8d4721c844b07a#platform%2Fpackages%2Fai-mate%2Fconversation-assistant%2Fsrc%2Fcommon%2Futils%2Fcontent-transformer%2Fmd-tokenizer%2Fmd-plugin-smartlink%2Findex.ts
// Right now the support is dropped in favour of supporting what the Canva Agent needs.
// It's likely first-class Enahnced Smart links  (e.g `(my canva link "smartCard-embed")[http://some.canva.link]`
// will be how we support this in the future
const linkRegex = {
	youtube: `${linkPatternStart}youtube\\.com${linkPatternEnd}`,
	youtubeShortUrl: `${linkPatternStart}youtu\\.be${linkPatternEnd}`,
	loomVideo: `${linkPatternStart}loom\\.com\\/share\\/${linkPatternEnd}`,
	figmaFile: `${linkPatternStart}figma\\.com\\/file\\/${linkPatternEnd}`,
	confluenceWhiteboard: `${linkPatternStart}.*\\/wiki\\/spaces\\/.*\\/whiteboard\\/${linkPatternEnd}`,
	googleDoc: `${linkPatternStart}docs\\.google\\.com\\/(document|presentation|spreadsheets)\\/d\\/${linkPatternEnd}`,
	googleForm: `${linkPatternStart}docs\\.google\\.com\\/forms\\/d\\/${linkPatternEnd}`,
	microsoftForm: `${linkPatternStart}forms\\.office\\.com\\/Pages\\/ResponsePage\\.aspx\\?id\\=${linkPatternEnd}`,
};
const supportedEmbedProviders = Object.values(linkRegex).join('|');
const embedRegex = new RegExp(supportedEmbedProviders, 'g');

const smartLinks = (state: StateCore) => {
	const tokens: Token[] = [];
	const embedTokens: Token[] = [];
	let embedMatches: string[] = [];

	for (const token of state.tokens) {
		const processedTokenChildren: Array<Token | null> = [];

		embedMatches = [...embedMatches, ...new Set(token.content?.trim().match(embedRegex))];

		// Any block or embed Smart Card links will be in an "inline" token. If
		// the current token is anything other than "inline", or there's no child tokens
		// to process, then we add it to the list of tokens to keep and move forward
		if (token.type !== 'inline' || (token.children && token?.children?.length <= 0)) {
			tokens.push(token);
			continue;
		}

		if (token.children) {
			token.children.forEach((childToken, i) => {
				const url = childToken.attrGet('href') ?? '';
				const discardedToken =
					['link_close', 'text'].includes(childToken.type) &&
					[processedTokenChildren[i - 1]?.type, processedTokenChildren[i - 2]?.type].includes(
						'inlineCard',
					);
				const isInlineLink = url === token.children?.[i + 1]?.content;

				// Discarded tokens are preserved as `null` so index
				// cross-referencing between processed and raw tokens may
				// be preserved, however null are filtered out later.
				if (discardedToken) {
					processedTokenChildren.push(null);
					return;
				}

				if (
					childToken?.type === 'link_open' &&
					(isSupportedByLinkingPlatform(url) || isInlineLink)
				) {
					let cardToken = new state.Token('inlineCard', '', 0);
					cardToken.content = '';
					cardToken.block = false;
					cardToken.level = childToken.level;
					cardToken.attrSet('url', `${url}`);
					processedTokenChildren.push(cardToken);
					return;
				}

				processedTokenChildren.push(childToken);
			});
		}

		const filteredTokenChildren = processedTokenChildren.filter(
			(childToken) => childToken !== null,
		) as Token[];
		token.children = filteredTokenChildren;
		tokens.push(token);
	}

	if (embedMatches?.length) {
		[...new Set(embedMatches)].forEach((match) => {
			let embedToken = new state.Token('embedCard', '', 0);
			embedToken.content = '';
			embedToken.block = true;
			embedToken.attrSet('url', `${match}`);
			embedToken.attrSet('layout', 'center');
			embedTokens.push(embedToken);
		});
	}
	state.tokens = [...tokens, ...embedTokens];

	return;
};

export default (md: MarkdownIt): void => md.core.ruler.push('inlineCard', smartLinks);
